import React, { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import WaitlistForm from '../../components/marketing/WaitlistForm';
import { useAnalyticsConsent } from '../../hooks/useAnalyticsConsent';
import { rememberWaitlistRef } from '../../services/waitlistService';
import { ANALYTICS_CONFIGURED } from '../../utils/analyticsConfigured';
import { onWaitlistJoined } from '../../utils/waitlistJoined';
import { NAV_CTA } from '../content/nav';
import { useHealthCheck } from '../health-check/HealthCheckProvider';
import { useNavMode } from '../layout/NavModeContext';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import useFocusTrap from '../hooks/useFocusTrap';
import useInert from '../hooks/useInert';
import matchesMedia from './matchesMedia';
import { browserStorage, canShowPopup, recordDismissed } from './popupEligibility';
import usePopupTrigger from './usePopupTrigger';

export const WAITLIST_DEPOSIT_PATH = '/waitlist/deposit';
export const DESKTOP_QUERY = '(pointer: fine) and (min-width: 768px)';

const isDesktop = () => matchesMedia(DESKTOP_QUERY);

/** Any input, textarea or select (of any type), or a contenteditable region. */
function isEditable(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  // isContentEditable already covers a contenteditable ancestor.
  return el.isContentEditable || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT';
}

/**
 * True when `el`, outside the popup, shows the visitor is busy with something
 * on the page: an editable field, or any control inside a form (the footer
 * waitlist form's submit button).
 */
function isEngagedOutside(el: Element | null, popup: RefObject<HTMLElement | null>): boolean {
  if (!(el instanceof Element) || popup.current?.contains(el)) return false;
  return isEditable(el) || el.closest('form') !== null;
}

/**
 * The join-the-waitlist popup. Mounted by MarketingLayout, inside the
 * health-check provider, only on the pages that opt in with `waitlistPopup`
 * (the hero pages and the FAQ), and portaled to <body>.
 *
 * It shows only when the rules allow it (popupEligibility), the visitor has
 * shown interest (usePopupTrigger), the cookie banner is out of the way, the
 * health-check panel and the phone menu are closed, and the visitor has not
 * started on a field or form on the page during this visit. On desktop it is
 * a modal dialog: focus is trapped, the page behind is inert and does not
 * scroll. On a phone it is a bottom slide-in that leaves the page usable, to
 * stay clear of Google's intrusive-interstitial guidance (check its height on
 * a small phone).
 *
 * A save goes through the shared WaitlistForm, so waitlist_signup fires once
 * exactly as it does everywhere else. Stripe never runs in here: the visitor
 * moves to /waitlist/deposit with the entry ref.
 *
 * z-index ladder: nav 40 · mobile menu 50 · ConsentBanner 50 · this backdrop
 * 58 · this popup 59 · health-check backdrop 60 · health-check panel 61.
 */
export default function WaitlistPopup() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const consent = useAnalyticsConsent();
  const { isOpen: healthCheckOpen } = useHealthCheck();
  const { menuOpen } = useNavMode();
  const [closed, setClosed] = useState(false);
  const [desktop, setDesktop] = useState(isDesktop);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Whether focus was inside the popup when it was asked to close. The sheet
  // is not modal, so only then is handing focus back the right thing to do.
  const focusInsideOnClose = useRef(false);
  // Sticky for the rest of the mount: once someone has started on a form on
  // the page, interrupting them is worse than missing the signup.
  const [engaged, setEngaged] = useState(false);

  // Only the path or a close can change the answer during a visit, so storage
  // is read once per change rather than on every render.
  const eligible = useMemo(
    () => !closed && canShowPopup(pathname, Date.now(), browserStorage()),
    [pathname, closed],
  );
  const triggered = usePopupTrigger(eligible);
  const bannerSettled = !ANALYTICS_CONFIGURED || consent !== null;
  // Never open on someone who has used a field or form on the page.
  const visible = eligible && triggered && bannerSettled && !healthCheckOpen && !menuOpen && !engaged;

  // Dialog or sheet follows the screen (a rotated tablet, a resized window),
  // but only while closed: swapping layout under someone mid-form is worse.
  useEffect(() => {
    if (visible || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(DESKTOP_QUERY);
    const update = () => setDesktop(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, [visible]);

  // Until it opens, watch where focus lands. Only focusin matters: nothing
  // ever clears the flag, so a focusout (moving to a submit button, iOS
  // dropping relatedTarget, switching windows) cannot release the hold. Once
  // open the watch stops, so moving to a field behind the phone sheet cannot
  // make it flicker.
  const watchFocus = eligible && !visible && !engaged;
  useEffect(() => {
    if (!watchFocus) return undefined;
    const check = (el: Element | null) => {
      if (isEngagedOutside(el, dialogRef)) setEngaged(true);
    };
    check(document.activeElement);
    const onFocusIn = (event: FocusEvent) => check(event.target as Element | null);
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, [watchFocus]);

  const close = useCallback(() => {
    focusInsideOnClose.current = !!dialogRef.current?.contains(document.activeElement);
    setClosed(true);
  }, []);

  // Joined through another form on this page (the hero, the footer): there is
  // nothing left to ask. The popup's own saves are handled by onSaved, which
  // keeps the form's confirmation up when there is no ref to move on with.
  useEffect(
    () =>
      onWaitlistJoined((surface) => {
        if (surface !== 'popup') close();
      }),
    [close],
  );

  const dismiss = useCallback(() => {
    recordDismissed(Date.now(), browserStorage());
    close();
  }, [close]);

  const onSaved = useCallback(
    (ref: string | null) => {
      // No ref means the form is showing its own confirmation. Leave it up.
      if (!ref) return;
      close();
      rememberWaitlistRef(ref);
      navigate(WAITLIST_DEPOSIT_PATH, { state: { ref } });
    },
    [close, navigate],
  );

  // Desktop: initial focus on the first field, not the close button.
  const firstField = useMemo<RefObject<HTMLElement | null>>(
    () => ({
      get current() {
        return dialogRef.current?.querySelector<HTMLElement>('form input:not([type="hidden"])') ?? null;
      },
    }),
    [],
  );

  useFocusTrap(dialogRef, visible && desktop, { onEscape: dismiss, initialFocus: firstField });
  useInert(visible && desktop);
  useBodyScrollLock(visible && desktop);

  // The phone slide-in is not modal. Focus moves to it when it opens so a
  // screen reader announces it, Escape still closes it, and focus goes back
  // only if it was still inside the sheet at the moment it closed.
  useEffect(() => {
    if (!visible || desktop) return undefined;
    const restoreTo = document.activeElement as HTMLElement | null;
    focusInsideOnClose.current = false;
    dialogRef.current?.focus({ preventScroll: true });
    const onKeyDown = (event: KeyboardEvent) => {
      // Escape inside a field on the page behind belongs to that field (say,
      // closing an autocomplete), not to the sheet.
      if (event.key === 'Escape' && !isEngagedOutside(event.target as Element | null, dialogRef)) dismiss();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (focusInsideOnClose.current && restoreTo && document.contains(restoreTo)) {
        restoreTo.focus?.({ preventScroll: true });
      }
    };
  }, [visible, desktop, dismiss]);

  if (!visible || typeof document === 'undefined') return null;

  return createPortal(
    <>
      {desktop && <div className="mk-wl-popup-backdrop" onClick={dismiss} aria-hidden="true" />}
      <div
        ref={dialogRef}
        className="mk-root mk-wl-popup"
        data-layout={desktop ? 'dialog' : 'sheet'}
        role="dialog"
        tabIndex={-1}
        aria-modal={desktop ? 'true' : 'false'}
        aria-labelledby="wl-popup-title"
      >
        <div className="mk-wl-popup__bar">
          <h2 id="wl-popup-title" className="mk-wl-popup__title t-details">
            {NAV_CTA.label}
          </h2>
          <button type="button" className="mk-wl-popup__close" onClick={dismiss} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path d="M2 2l14 14M16 2L2 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <WaitlistForm
          sourceSurface="popup"
          variant="popup"
          afterSave="navigate"
          submitLabel="Join"
          onSuccess={onSaved}
        />
      </div>
    </>,
    document.body,
  );
}
